/* eslint-disable react/no-unknown-property -- Firebolt consumes the custom css prop. */
import { css } from '@firebolt-dev/css'
import { useEffect, useRef, useState } from 'react'
import { AlertTriangleIcon, CheckCircleIcon, InfoIcon, ShieldAlertIcon, XIcon } from 'lucide-react'

import { COLORS, GLASS, ICON_SIZES, RADII, SHADOWS, TYPE, space } from '../theme'
import { focusTrapIndex } from '../notification-queue'

const EMPTY = { modal: null, toasts: [], queued: 0 }

export function NotificationCenter({ world }) {
  const [state, setState] = useState(() => world?.ui?.notifications?.snapshot?.() || EMPTY)
  useEffect(() => {
    const update = value => setState(value || EMPTY)
    world.on('notifications', update)
    update(world.ui.notifications.snapshot())
    return () => world.off('notifications', update)
  }, [world])
  return <>
    <div aria-live='polite' aria-relevant='additions' css={toastStackCss}>{state.toasts.map(item => <ToastItem key={item.id} item={item} dismiss={() => world.ui.notifications.dismiss(item.id)} />)}</div>
    {state.modal && <ModalItem key={state.modal.id} item={state.modal} queue={world.ui.notifications} />}
  </>
}

function ToastItem({ item, dismiss }) {
  const Icon = item.tone === 'error' ? ShieldAlertIcon : item.tone === 'warning' ? AlertTriangleIcon : item.tone === 'success' ? CheckCircleIcon : InfoIcon
  return <article role={item.tone === 'error' ? 'alert' : 'status'} css={toastCss(item.tone)}>
    <Icon size={ICON_SIZES.sm} /><div><strong>{item.title || item.kind.toUpperCase()}</strong><p>{item.message}</p></div>
    {item.dismissible && <button type='button' aria-label='Закрыть уведомление' onClick={dismiss}><XIcon size={ICON_SIZES.xs} /></button>}
  </article>
}

function ModalItem({ item, queue }) {
  const dialogRef = useRef(null)
  const previousFocus = useRef(null)
  useEffect(() => {
    previousFocus.current = document.activeElement
    const dialog = dialogRef.current
    const focusables = () => [...dialog.querySelectorAll('button:not([disabled]),[href],input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])')]
    focusables()[0]?.focus()
    const onKey = event => {
      const list = focusables()
      if (event.key === 'Tab' && list.length) {
        const index = Math.max(0, list.indexOf(document.activeElement))
        event.preventDefault(); event.stopPropagation(); list[focusTrapIndex(index, list.length, event.shiftKey)]?.focus()
      } else if (event.key === 'Escape' && item.dismissible) {
        event.preventDefault(); event.stopPropagation(); queue.respond(item.id, false)
      } else if (event.key === 'Enter' && item.safeEnter && document.activeElement?.tagName !== 'TEXTAREA') {
        event.preventDefault(); event.stopPropagation(); queue.respond(item.id, true)
      }
    }
    document.addEventListener('keydown', onKey, true)
    return () => {
      document.removeEventListener('keydown', onKey, true)
      if (previousFocus.current?.isConnected) previousFocus.current.focus()
    }
  }, [item, queue])
  return <div css={backdropCss} onPointerDown={event => event.stopPropagation()} onClick={event => event.stopPropagation()}>
    <section ref={dialogRef} role='alertdialog' aria-modal='true' aria-labelledby={`${item.id}-title`} aria-describedby={`${item.id}-message`} tabIndex={-1} css={modalCss(item.tone)}>
      <div css={modalIconCss}>{item.kind === 'security' ? <ShieldAlertIcon size={ICON_SIZES.lg} /> : <AlertTriangleIcon size={ICON_SIZES.lg} />}</div>
      <h2 id={`${item.id}-title`}>{item.title || 'ПОДТВЕРЖДЕНИЕ'}</h2>
      <p id={`${item.id}-message`}>{item.message}</p>
      <div css={actionsCss}>
        <button type='button' onClick={() => queue.respond(item.id, false)} css={secondaryCss}>{item.cancelText}</button>
        <button type='button' onClick={() => queue.respond(item.id, true)} css={primaryCss(item.tone)}>{item.confirmText}</button>
      </div>
    </section>
  </div>
}

const toastStackCss = css`position:absolute;z-index:9000;top:calc(${space(3)} + env(safe-area-inset-top));right:calc(${space(3)} + env(safe-area-inset-right));width:min(420px,calc(100vw - ${space(4)}));display:flex;flex-direction:column;gap:${space(1)};pointer-events:none;@media(max-width:600px){top:auto;bottom:calc(${space(3)} + env(safe-area-inset-bottom));right:${space(2)};}`
const toneColor = tone => tone === 'error' ? COLORS.error : tone === 'warning' ? COLORS.warning : tone === 'success' ? COLORS.success : COLORS.cyan
const toastCss = tone => css`pointer-events:auto;display:grid;grid-template-columns:auto 1fr auto;gap:${space(1)};align-items:start;padding:${space(2)};border:1px solid ${toneColor(tone)};border-radius:${RADII.xl};background:${COLORS.bgPanel};backdrop-filter:${GLASS.base};box-shadow:${SHADOWS.raised};color:${toneColor(tone)};& strong{display:block;font-size:${TYPE.caption.size};letter-spacing:.1em;}& p{margin:4px 0 0;color:${COLORS.text};font-size:${TYPE.body.size};line-height:1.4;overflow-wrap:anywhere;}& button{border:0;background:transparent;color:${COLORS.textSecondary};cursor:pointer;padding:4px;}`
const backdropCss = css`position:absolute;inset:0;z-index:9500;pointer-events:auto;display:grid;place-items:center;padding:${space(2)};background:rgba(3,7,13,.76);backdrop-filter:${GLASS.base};touch-action:none;`
const modalCss = tone => css`width:min(520px,100%);max-height:calc(100dvh - ${space(4)});overflow:auto;padding:${space(4)};border:1px solid ${toneColor(tone)};border-radius:${RADII.xxl};background:${COLORS.bgPanel};box-shadow:${SHADOWS.panel};color:${COLORS.text};outline:none;& h2{margin:${space(2)} 0 ${space(1)};font-size:${TYPE.title.size};letter-spacing:${TYPE.title.spacing};}& p{margin:0;color:${COLORS.textSecondary};font-size:${TYPE.body.size};line-height:1.55;overflow-wrap:anywhere;}`
const modalIconCss = css`color:${COLORS.warning};`
const actionsCss = css`display:grid;grid-template-columns:1fr 1fr;gap:${space(1)};margin-top:${space(4)};@media(max-width:420px){grid-template-columns:1fr;}`
const buttonBase = css`min-height:48px;border-radius:${RADII.lg};font-weight:750;letter-spacing:.08em;cursor:pointer;`
const secondaryCss = css`${buttonBase};border:1px solid ${COLORS.borderStrong};background:${COLORS.bgElevated};color:${COLORS.text};`
const primaryCss = tone => css`${buttonBase};border:1px solid ${toneColor(tone)};background:${toneColor(tone)};color:${COLORS.bgDeep};`
